// client/src/components/MerchantDashboard.js
// === Optimisation hybride: sans changer l'UI ni la logique métier ===

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  getDoc,
  getDocFromCache, // [COST-OPT] lire cache avant réseau
  limit as fsLimit,
  orderBy
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged
} from 'firebase/auth';
import {
  ref,
  listAll,
  getMetadata,
  getDownloadURL,
  uploadBytes
} from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import '../CSS/Dashboard.css';
import { FaStar } from 'react-icons/fa';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
} from 'chart.js';
import { sanitize } from '../../utils/sanitize';
import { listCompanyShares } from '../../utils/shareStore';

// --- utils locaux ---
function debounce(fn, delay = 1500) {
  let t;
  function wrapper(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), delay);
  }
  wrapper.cancel = () => clearTimeout(t);
  return wrapper;
}

// [HYBRID-OPT] Cache de pseudos en mémoire + localStorage (TTL 24h) pour limiter les getDocs
const PSEUDO_CACHE_KEY = 'pseudoCache_v1';
function loadPseudoCache() {
  try {
    const raw = localStorage.getItem(PSEUDO_CACHE_KEY);
    if (!raw) return { data: {}, ts: 0 };
    const parsed = JSON.parse(raw);
    // TTL 24h
    if (Date.now() - (parsed.ts || 0) > 24 * 3600 * 1000) return { data: {}, ts: 0 };
    return parsed;
  } catch {
    return { data: {}, ts: 0 };
  }
}
function savePseudoCache(cache) {
  try {
    localStorage.setItem(PSEUDO_CACHE_KEY, JSON.stringify({ data: cache, ts: Date.now() }));
  } catch {}
}

// Normalise les champs date (Timestamp, Date, secondes...) en objet Date
const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    try { return value.toDate(); } catch { return null; }
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const millis = value.seconds * 1000 + (value.nanoseconds || 0) / 1_000_000;
    const d = new Date(millis);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) {
    const d = new Date(asNumber);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const resolveFeedbackDate = (data) => {
  const primary = toDateSafe(data?.createdAt);
  if (primary) return primary;
  const fallback = toDateSafe(data?.txDateMillis);
  return fallback;
};

// [HYBRID-OPT] Comparateur léger pour éviter des setState inutiles
const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

const STAR_KEYS = [1, 2, 3, 4, 5];
const computeSatisfactionState = (raw) => {
  const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  if (!raw) return { average: 0, counts };

  let totalRatings = 0;
  let totalScore = 0;

  if (raw.counts && typeof raw.counts === 'object') {
    for (let i = 0; i < STAR_KEYS.length; i++) {
      const star = STAR_KEYS[i];
      const value = Number(raw.counts[star] ?? raw.counts[String(star)]) || 0;
      counts[star] = value;
      totalRatings += value;
      totalScore += value * star;
    }
  } else if (Array.isArray(raw.ratings)) {
    for (let i = 0; i < raw.ratings.length; i++) {
      const r = Number(raw.ratings[i]?.rating);
      if (r >= 1 && r <= 5) {
        counts[r] = (counts[r] || 0) + 1;
        totalRatings += 1;
        totalScore += r;
      }
    }
  }

  if (totalRatings === 0) {
    const docTotal = Number(raw.totalRatings) || 0;
    const docScore = Number(raw.totalScore) || 0;
    if (docTotal > 0) {
      totalRatings = docTotal;
      totalScore = docScore;
    }
  }

  const average = totalRatings > 0 ? totalScore / totalRatings : 0;
  return { average, counts };
};

// Initialisation des statistiques depuis le localStorage pour un affichage instantané
const initialStats = (() => {
  try {
    const raw = localStorage.getItem('stats');
    return raw ? JSON.parse(raw) : { count: 0, countInMetadata: 0, employeesStats: [] };
  } catch {
    return { count: 0, countInMetadata: 0, employeesStats: [] };
  }
})();

// [COST-OPT] Cache 24h de l'URL du logo pour éviter listAll/getMetadata/download à chaque chargement
const LOGO_CACHE_KEY = (uid) => `profilePhotoURL_${uid}`;
const LOGO_CACHE_TTL = 24 * 3600 * 1000;
// Suppression de l'index Firestore : cache local uniquement
const DASH_INDEX_TTL_MS = 10 * 60 * 1000; // 10 minutes : stats peu volatiles mais utiles
const dashCacheKey = (uid) => `merchant_dash_index_v2_${uid}`;
const nowMs = () => Date.now();

const serializeDashPayload = (payload = {}) => ({
  count: payload.count || 0,
  countInMetadata: payload.countInMetadata || 0,
  employeesStats: Array.isArray(payload.employeesStats) ? payload.employeesStats : [],
  updatedAt: payload.updatedAt || nowMs(),
});

const loadDashCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(dashCacheKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts || 0;
    if (!ts || nowMs() - ts > DASH_INDEX_TTL_MS) return null;
    return parsed.payload || null;
  } catch {
    return null;
  }
};

const saveDashCache = (uid, payload) => {
  if (!uid || !payload) return;
  try {
    localStorage.setItem(
      dashCacheKey(uid),
      JSON.stringify({ ts: payload.updatedAt || nowMs(), payload: serializeDashPayload(payload) })
    );
  } catch {}
};


const MerchantDashboard = () => {
  // États de l'utilisateur, pseudo et modales
  const [user, setUser] = useState(null);
  const [newPseudo, setNewPseudo] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [profilePhotoURL, setProfilePhotoURL] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);

  // États des statistiques
  const [count, setCount] = useState(initialStats.count);
  const [countInMetadata, setCountInMetadata] = useState(initialStats.countInMetadata);
  const [employeesStats, setEmployeesStats] = useState(initialStats.employeesStats);

  // Nouvel état pour la satisfaction
  const [satisfactionData, setSatisfactionData] = useState({
    average: 0,
    counts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });
  const [feedbacks, setFeedbacks] = useState([]);

  // Nouvel état pour la fidélité
  const [fidelityData, setFidelityData] = useState({
    clients: [],
    total: 0,
    totalEvents: 0,
    uniqueClients: 0,
    timeline: { labels: [], counts: [] },
    summary: {
      recentVisits: 0,
      recentClients: 0,
      newClients: 0,
      returningClients: 0,
      previousVisits: 0,
      bestDay: null,
      currentDayUniqueClients: 0,
      currentDayNewClients: 0
    },
    byUid: {},
    pointsByUid: {},
    totalPointsBalance: 0,
    totalPointsEarned: 0
  });
  const [fidelityProgramType, setFidelityProgramType] = useState('Fidbycount');
  const [sortedFidelityEntries, setSortedFidelityEntries] = useState([]);

  // Autres états
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [profilePhotoFetched, setProfilePhotoFetched] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [dashIndexReady, setDashIndexReady] = useState(false);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const mountedRef = useRef(true);
  const isPointsProgram = fidelityProgramType === 'Fidbypoints';
  const dashSigRef = useRef('');
  const dashTsRef = useRef(0);

  // [HYBRID-OPT] Références/queries mémoïsées
  const entreprisesCol = useMemo(() => collection(firestore, 'entreprises'), []);

  // [HYBRID-OPT] Cache pseudos en ref
  const pseudoCacheRef = useRef(loadPseudoCache().data);

  // Helpers dépendants des hooks
  const applyDashPayload = useCallback((payload) => {
    if (!payload) return;
    setCount((prev) => (prev === payload.count ? prev : payload.count || 0));
    setCountInMetadata((prev) => (prev === payload.countInMetadata ? prev : payload.countInMetadata || 0));
    if (Array.isArray(payload.employeesStats)) {
      setEmployeesStats((prev) => {
        const prevSig = JSON.stringify(prev || []);
        const nextSig = JSON.stringify(payload.employeesStats);
        return prevSig === nextSig ? prev : payload.employeesStats;
      });
    }
    try {
      localStorage.setItem('stats', JSON.stringify({
        count: payload.count || 0,
        countInMetadata: payload.countInMetadata || 0,
        employeesStats: payload.employeesStats || []
      }));
    } catch {}
  }, []);

  const persistDashIndex = useCallback((uid, payload) => {
    if (!uid || !payload) return;
    const normalized = serializeDashPayload(payload);
    const sig = JSON.stringify(normalized);
    if (dashSigRef.current === sig) return;
    dashSigRef.current = sig;
    saveDashCache(uid, normalized);
    dashTsRef.current = normalized.updatedAt;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Type de programme fidélité (passages vs points)
  useEffect(() => {
    if (!user?.uid) {
      setFidelityProgramType('Fidbycount');
      return;
    }
    const fidelityDocRef = doc(firestore, 'fidelity', user.uid);
    const unsubscribeFidelityType = onSnapshot(
      fidelityDocRef,
      (snapshot) => {
        if (!mountedRef.current) return;
        if (snapshot.exists()) {
          const type = snapshot.data()?.Type || 'Fidbycount';
          setFidelityProgramType((prev) => (prev === type ? prev : type));
        } else {
          setFidelityProgramType('Fidbycount');
        }
      },
      (error) => console.error('Erreur onSnapshot type fidélité :', error)
    );
    return () => unsubscribeFidelityType();
  }, [user?.uid]);

  // Recompte initial si index manquant/stale
  useEffect(() => {
    if (!user?.uid || !dashIndexReady) return;
    const run = async () => {
      if (dashSigRef.current && nowMs() - dashTsRef.current < DASH_INDEX_TTL_MS) return;
      try {
        await recountInvoices({ userId: user.uid });
      } catch (e) {
        console.warn('Recount initial error:', e);
      }
    };
    run();
    // eslint-disable-next-line
  }, [user?.uid, dashIndexReady]);

  // Vérification de la fiche entreprise (one-shot, limité à 1 doc)
  const checkCompany = useCallback(async (userId) => {
    const qEnt = query(entreprisesCol, where('userId', '==', userId), fsLimit(1)); // [HYBRID-OPT]
    const querySnapshot = await getDocs(qEnt);
    if (querySnapshot.empty) {
      setShowStripeModal(true);
    } else {
      const entrepriseData = querySnapshot.docs[0].data();
      setShowStripeModal(!(entrepriseData.base === 'active'));
    }
  }, [entreprisesCol]);

  // ✅ Authentification et snapshot utilisateur
  useEffect(() => {
    let unsubscribeUserDoc = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (typeof unsubscribeUserDoc === 'function') {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      if (currentUser) {
        const userId = currentUser.uid;
        const userDocRef = doc(firestore, 'users', userId);

        // [COST-OPT] hydrate depuis le cache Firestore si dispo (aucun read réseau)
        try {
          const cached = await getDocFromCache(userDocRef);
          if (cached?.exists()) {
            const cachedData = cached.data() || {};
            setUser((prev) => {
              const next = { ...cachedData, uid: userId };
              return shallowEqual(prev, next) ? prev : next;
            });
            if (!cachedData.pseudo) setShowModal(true);
          }
        } catch {}

        unsubscribeUserDoc = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (!mountedRef.current) return;
            if (!docSnapshot.exists()) {
              setUser({ uid: userId });
              setShowModal(true);
              return;
            }
            const userData = docSnapshot.data() || {};
            // [HYBRID-OPT] éviter setState si inchangé
            setUser((prev) => {
              const next = { ...userData, uid: userId };
              return shallowEqual(prev, next) ? prev : next;
            });
            if (!userData.pseudo) setShowModal(true);
          },
          (err) => console.error('Erreur snapshot user :', err)
        );

        checkCompany(userId);
      } else {
        navigate('/logingeneral');
      }
    });

    return () => {
      if (typeof unsubscribeUserDoc === 'function') unsubscribeUserDoc();
      unsubscribeAuth();
    };
  }, [auth, navigate, checkCompany]);

  // Chargement index (cache puis Firestore) + onSnapshot pour stats
  useEffect(() => {
    setDashIndexReady(false);
    if (!user?.uid) return;

    const cached = loadDashCache(user.uid);
    if (cached) {
      applyDashPayload(cached);
      dashSigRef.current = JSON.stringify(serializeDashPayload(cached));
      dashTsRef.current = cached.updatedAt || nowMs();
    }

    // Index Firestore supprimé : on ne souscrit plus à merchantDashboardIndex
    setDashIndexReady(true);
  }, [user?.uid, applyDashPayload]);

  // Récupération des données de satisfaction
  useEffect(() => {
    if (!user?.uid) return;
    const satisfactionDocRef = doc(firestore, 'Satisfaction', user.uid);

    // [COST-OPT] hydrate depuis cache si dispo
    (async () => {
      try {
        const cached = await getDocFromCache(satisfactionDocRef);
        if (cached?.exists()) {
          const next = computeSatisfactionState(cached.data() || {});
          setSatisfactionData((prev) => shallowEqual(prev, next) ? prev : next);
        }
      } catch {}
    })();

    const unsubscribeSatisfaction = onSnapshot(
      satisfactionDocRef,
      (docSnapshot) => {
        if (!mountedRef.current) return;
        if (docSnapshot.exists()) {
          const next = computeSatisfactionState(docSnapshot.data() || {});
          setSatisfactionData((prev) => shallowEqual(prev, next) ? prev : next);
        } else {
          const next = { average: 0, counts: { 5:0,4:0,3:0,2:0,1:0 } };
          setSatisfactionData((prev) => shallowEqual(prev, next) ? prev : next);
        }
      },
      (error) => console.error('Erreur onSnapshot Satisfaction :', error)
    );
    return () => unsubscribeSatisfaction();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const feedbacksRef = collection(firestore, 'Satisfaction', user.uid, 'feedbacks');
    const feedbacksQuery = query(feedbacksRef, orderBy('createdAt', 'desc'), fsLimit(1000));

    const unsubscribe = onSnapshot(
      feedbacksQuery,
      (snapshot) => {
        if (!mountedRef.current) return;
        const entries = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const createdAt = resolveFeedbackDate(data);
          const comment = typeof data.comment === 'string' ? data.comment : '';
          const pseudo = typeof data.pseudo === 'string' ? data.pseudo : 'Anonyme';
          const txDateMs = typeof data.txDateMillis === 'number' ? data.txDateMillis : 0;
          const sortTs = createdAt?.getTime() || txDateMs || 0;
          entries.push({
            id: docSnap.id,
            rating: Number(data.rating) || 0,
            comment,
            pseudo,
            source: data.source || 'client',
            createdAt,
            txDateMillis: txDateMs,
            sortTs
          });
        });
        entries.sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0));
        setFeedbacks(entries);
      },
      (error) => console.error('Erreur onSnapshot feedbacks :', error)
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Récupération des données de fidélité
  useEffect(() => {
    if (!user?.uid) return;
    const fidelityListRef = collection(firestore, 'fidelity', user.uid, 'list');

    // [COST-OPT] pas de query compliquée ici (modèle actuel), mais hydrate cache si dispo
    // (Firestore ne met pas en cache de collection entière simplement via getDocFromCache, on reste donc en live)

    const unsubscribeFidelity = onSnapshot(
      fidelityListRef,
      (querySnapshot) => {
        if (!mountedRef.current) return;

        const clientsMap = Object.create(null);
        let totalCount = 0;
        let totalEvents = 0;
        const eventsByDay = Object.create(null);
        const clientsByDay = Object.create(null);
        const byUid = Object.create(null);
        const pointsByUid = Object.create(null);
        let totalPointsEarned = 0;

        const normalizeToDate = (value) => {
          if (!value) return null;
          if (typeof value.toDate === 'function') {
            try { return value.toDate(); } catch { return null; }
          }
          if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
            return new Date(value.seconds * 1000 + value.nanoseconds / 1_000_000);
          }
          if (value instanceof Date) return value;
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        };

        const formatDateKey = (date) => {
          const y = date.getFullYear();
          const m = date.getMonth() + 1;
          const d = date.getDate();
          return `${y}-${m < 10 ? '0' + m : m}-${d < 10 ? '0' + d : d}`;
        };

        const ensureClientEntry = (clientId, clientData) => {
          let entry = clientsMap[clientId];
          if (entry) return entry;
          const label =
            clientData?.pseudo ||
            clientData?.name ||
            clientData?.prenom ||
            clientData?.Nom ||
            clientData?.clientName ||
            clientData?.email ||
            clientId;
          entry = {
            id: clientId,
            label,
            totalVisits: 0,
            firstVisit: null,
            lastVisit: null,
            points: clientData?.points || 0
          };
          clientsMap[clientId] = entry;
          return entry;
        };

        const normalizePointsValue = (value) => {
          if (value === null || value === undefined) return 0;
          const parsed = parseFloat(String(value).replace(',', '.'));
          return Number.isFinite(parsed) ? parsed : 0;
        };

        const recordEvent = (entry, clientId, clientData) => {
          if (!entry) return;
          const visitCount = Number(entry?.count) || 1;
          const rawDate =
            entry.date ||
            entry.timestamp ||
            entry.datetime ||
            entry.createdAt ||
            entry.time ||
            entry.created_at;
          const jsDate = normalizeToDate(rawDate);
          if (!jsDate) return;

          jsDate.setHours(0, 0, 0, 0);
          const key = formatDateKey(jsDate);

          const clientEntry = ensureClientEntry(clientId, clientData);
          clientEntry.totalVisits += visitCount;
          if (!clientEntry.lastVisit || jsDate > clientEntry.lastVisit) clientEntry.lastVisit = jsDate;
          if (!clientEntry.firstVisit || jsDate < clientEntry.firstVisit) clientEntry.firstVisit = jsDate;

          totalCount += visitCount;
          totalEvents += 1;
          eventsByDay[key] = (eventsByDay[key] || 0) + visitCount;

          let setForDay = clientsByDay[key];
          if (!setForDay) {
            setForDay = new Set();
            clientsByDay[key] = setForDay;
          }
          setForDay.add(clientId);

          const cashierUid = entry?.uid || 'Inconnu';
          byUid[cashierUid] = (byUid[cashierUid] || 0) + 1;
        };

        const recordPointsEntry = (entry) => {
          if (!entry) return;
          const value = normalizePointsValue(entry.points);
          if (value <= 0) return;
          const cashierUid = entry?.uid || 'Inconnu';
          pointsByUid[cashierUid] = (pointsByUid[cashierUid] || 0) + value;
          totalPointsEarned += value;
        };

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const clientId = docSnap.id;
          ensureClientEntry(clientId, data);

          if (Array.isArray(data.timestamps)) {
            for (let i = 0; i < data.timestamps.length; i++) recordEvent(data.timestamps[i], clientId, data);
          }
          if (Array.isArray(data.resetstamp)) {
            for (let i = 0; i < data.resetstamp.length; i++) recordEvent(data.resetstamp[i], clientId, data);
          }
          if (Array.isArray(data.pointshistory)) {
            for (let i = 0; i < data.pointshistory.length; i++) recordPointsEntry(data.pointshistory[i]);
          }
        });

        // Timeline compacte
        const dateKeys = Object.keys(eventsByDay);
        let labels = [];
        let counts = [];
        if (dateKeys.length > 0) {
          dateKeys.sort();
          const start = new Date(dateKeys[0]);
          const last = new Date(dateKeys[dateKeys.length - 1]);
          start.setHours(0, 0, 0, 0);
          last.setHours(0, 0, 0, 0);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const end = today > last ? today : last;

          const tmpLabels = [];
          const tmpCounts = [];
          for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
            const k = formatDateKey(cursor);
            tmpLabels.push(k);
            tmpCounts.push(eventsByDay[k] || 0);
          }
          labels = tmpLabels;
          counts = tmpCounts;
        }

        const clientsArray = Object.values(clientsMap);
        const uniqueClients = clientsArray.length;
        const totalPointsBalance = clientsArray.reduce(
          (sum, client) => sum + normalizePointsValue(client.points),
          0
        );

        // Fenêtre 30j
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const windowDays = 30;
        const windowStart = new Date(today); windowStart.setDate(windowStart.getDate() - (windowDays - 1));
        const previousStart = new Date(windowStart); previousStart.setDate(previousStart.getDate() - windowDays);
        const previousEnd = new Date(windowStart); previousEnd.setDate(previousEnd.getDate() - 1);

        const recentClientsSet = new Set();
        let recentVisits = 0;
        let previousVisits = 0;

        for (const k in eventsByDay) {
          const [Y, M, D] = k.split('-');
          const currentDate = new Date(+Y, (+M || 1) - 1, +D || 1);
          currentDate.setHours(0, 0, 0, 0);
          const countForDay = eventsByDay[k] || 0;
          if (currentDate >= windowStart && currentDate <= today) {
            recentVisits += countForDay;
            const set = clientsByDay[k];
            if (set) for (const uid of set) recentClientsSet.add(uid);
          } else if (currentDate >= previousStart && currentDate <= previousEnd) {
            previousVisits += countForDay;
          }
        }

        const newClientsSet = new Set();
        for (let i = 0; i < clientsArray.length; i++) {
          const c = clientsArray[i];
          if (c.firstVisit && c.firstVisit >= windowStart && c.firstVisit <= today) newClientsSet.add(c.id);
        }

        const todayKey = (() => {
          const y = today.getFullYear();
          const m = today.getMonth() + 1;
          const d = today.getDate();
          return `${y}-${m < 10 ? '0' + m : m}-${d < 10 ? '0' + d : d}`;
        })();
        const uniqueClientsToday = clientsByDay[todayKey] ? clientsByDay[todayKey].size : 0;

        let newClientsToday = 0;
        for (let i = 0; i < clientsArray.length; i++) {
          const c = clientsArray[i];
          if (!c.firstVisit) continue;
          const key = (() => {
            const y = c.firstVisit.getFullYear();
            const m = c.firstVisit.getMonth() + 1;
            const d = c.firstVisit.getDate();
            return `${y}-${m < 10 ? '0' + m : m}-${d < 10 ? '0' + d : d}`;
          })();
          if (key === todayKey) newClientsToday++;
        }

        let recentReturningCount = 0;
        for (const id of recentClientsSet) if (!newClientsSet.has(id)) recentReturningCount++;

        let bestDay = null;
        if (labels.length) {
          let maxCount = 0;
          for (let i = 0; i < labels.length; i++) {
            const c = counts[i] || 0;
            if (c > maxCount) { maxCount = c; bestDay = { date: labels[i], count: c }; }
          }
          if (maxCount === 0) bestDay = null;
        }

        const next = {
          clients: clientsArray,
          total: totalCount,
          totalEvents,
          uniqueClients,
          timeline: { labels, counts },
          summary: {
            recentVisits,
            recentClients: recentClientsSet.size,
            newClients: newClientsSet.size,
            returningClients: recentReturningCount,
            previousVisits,
            bestDay,
            currentDayUniqueClients: uniqueClientsToday,
            currentDayNewClients: newClientsToday
          },
          byUid,
          pointsByUid,
          totalPointsBalance,
          totalPointsEarned
        };

        setFidelityData((prev) => shallowEqual(prev, next) ? prev : next); // [HYBRID-OPT]
      },
      (error) => console.error('Erreur onSnapshot Fidélité :', error)
    );
    return () => unsubscribeFidelity();
  }, [user?.uid]);

  // Tri des données de fidélité + fetch pseudos employés (with cache)
  useEffect(() => {
    const sourceByUid = isPointsProgram ? fidelityData?.pointsByUid : fidelityData?.byUid;
    if (!user?.uid || !sourceByUid) return;
    const uids = Object.keys(sourceByUid || {});
    if (!uids.length) {
      setSortedFidelityEntries([]);
      return;
    }

    (async () => {
      const pseudoMap = { ...pseudoCacheRef.current };
      const needFetch = uids.filter((uid) => uid && uid !== 'Inconnu' && !pseudoMap[uid]);

      // Lectures individuelles (getDoc) plutôt que batch `where in` : Firestore rejette
      // la requête list entière si un seul UID ne matche pas la règle de lecture du doc
      // (ex: cashier d'un autre marchand, compte supprimé). Ici, un échec par UID est isolé.
      await Promise.all(needFetch.map(async (uid) => {
        try {
          const ds = await getDoc(doc(firestore, 'users', uid));
          pseudoMap[uid] = ds.exists() ? (ds.data()?.pseudo || 'Inconnu') : 'Inconnu';
        } catch {
          pseudoMap[uid] = 'Inconnu';
        }
      }));

      // Maj cache local si on a appris des choses
      if (needFetch.length) {
        pseudoCacheRef.current = { ...pseudoCacheRef.current, ...pseudoMap };
        savePseudoCache(pseudoCacheRef.current);
      }

      const entries = uids.map((uid) => ({
        uid,
        count: sourceByUid[uid],
        pseudo:
          uid === user.uid
            ? user.pseudo || 'Vous'
            : pseudoMap[uid] || (uid ? 'Inconnu' : 'Inconnu')
      }));

      const currentEntry = entries.find((e) => e.uid === user.uid);
      const others = entries
        .filter((e) => e.uid !== user.uid)
        .sort((a, b) => (a.pseudo || '').localeCompare(b.pseudo || ''));
      const sorted = currentEntry ? [currentEntry, ...others] : others;

      setSortedFidelityEntries(sorted);
    })();
    // eslint-disable-next-line
  }, [user?.uid, user?.pseudo, fidelityData, isPointsProgram]);

  // Batch pour récupérer les pseudos employés (chunks de 10) — réutilise le cache
  // Regroupe les comptes : propres factures (Storage) + factures employés (companyShares)
  // Groupement par takenByPseudo — même logique que MerchantInvoices
  const recountInvoices = useCallback(async ({ userId }) => {
    // 1) Propres factures (simple list Storage, pas de getMetadata)
    let myCount = 0;
    try {
      const myList = await listAll(ref(storage, `${userId}/photos`));
      myCount = myList.items.length;
    } catch { /* dossier vide ou inexistant */ }

    // 2) Factures par employé via companyShares — groupe par takenByPseudo
    try {
      const sharedEntries = await listCompanyShares(userId);

      // Passe 1 : groupe les entrées qui ont déjà takenByPseudo en Firestore (rapide)
      const countByPseudo = {};
      const noPseudo = []; // entrées sans takenByPseudo → fallback getMetadata

      for (const entry of sharedEntries) {
        const pseudo = entry.takenByPseudo;
        if (pseudo) {
          countByPseudo[pseudo] = (countByPseudo[pseudo] || 0) + 1;
        } else {
          noPseudo.push(entry);
        }
      }

      // Passe 2 : pour les entrées sans takenByPseudo, getMetadata Storage (1 appel / fichier max)
      await Promise.all(
        noPseudo.map(async (entry) => {
          const storagePath = entry.storagePath || `${entry.ownerUid}/photos/${entry.fileName || entry.name}`;
          try {
            const md = await getMetadata(ref(storage, storagePath));
            const pseudo = md?.customMetadata?.TakenByPseudo;
            const key = pseudo || entry.ownerUid || 'Inconnu';
            countByPseudo[key] = (countByPseudo[key] || 0) + 1;
          } catch {
            const key = entry.ownerUid || 'Inconnu';
            countByPseudo[key] = (countByPseudo[key] || 0) + 1;
          }
        })
      );

      const totalInMetadata = Object.values(countByPseudo).reduce((a, b) => a + b, 0);
      const stats = Object.entries(countByPseudo)
        .filter(([, count]) => count > 0)
        .map(([pseudo, count]) => ({ pseudo, count }))
        .sort((a, b) => a.pseudo.localeCompare(b.pseudo));

      setCount((prev) => (prev === myCount ? prev : myCount));
      setCountInMetadata((prev) => (prev === totalInMetadata ? prev : totalInMetadata));
      setEmployeesStats(stats);

      persistDashIndex(userId, {
        count: myCount,
        countInMetadata: totalInMetadata,
        employeesStats: stats,
        updatedAt: nowMs(),
      });
    } catch (err) {
      console.error('recountInvoices error:', err);
      setCount((prev) => (prev === myCount ? prev : myCount));
    }
  }, [persistDashIndex]);

  // Listener onSnapshot entreprises (debounced recount + évite recalcul si invitations inchangées)
  useEffect(() => {
    if (!user?.uid) return;

    const qEnt = query(entreprisesCol, where('userId', '==', user.uid), fsLimit(1));

    const debounced = debounce(async () => {
      if (nowMs() - dashTsRef.current < DASH_INDEX_TTL_MS && dashSigRef.current) return;
      await recountInvoices({ userId: user.uid });
    }, 1000);

    const unsubscribeEntreprise = onSnapshot(
      qEnt,
      async (querySnapshot) => {
        if (querySnapshot.empty) return;
        debounced.cancel?.();
        debounced();
      },
      (error) => console.error('Erreur onSnapshot entreprises :', error)
    );

    return () => {
      debounced.cancel?.();
      unsubscribeEntreprise();
    };
  }, [user?.uid, entreprisesCol, recountInvoices]);

  // Récupération de la photo de profil (avec cache 24h)
  const fetchProfilePhoto = useCallback(async () => {
    if (!user?.uid) return;
    const cacheKey = LOGO_CACHE_KEY(user.uid);

    // 1) Affiche le cache si frais
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.url && parsed?.ts && Date.now() - parsed.ts < LOGO_CACHE_TTL) {
          setProfilePhotoURL(parsed.url);
          setProfilePhotoFetched(true);
          return; // [COST-OPT] pas de hit Storage
        } else if (parsed?.url) {
          // on peut afficher l'ancien le temps de rafraîchir
          setProfilePhotoURL(parsed.url);
        }
      }
    } catch {}

    try {
      const profilePhotoRef = ref(storage, `${user.uid}/photo-profil/`);
      const result = await listAll(profilePhotoRef);
      if (result.items.length > 0) {
        // [HYBRID-OPT] éviter getMetadata sur tout: on trie par nom si préfixé par timestamp
        let latestItem = null;

        const candidates = result.items
          .map((it) => ({ it, n: Number((it.name || '').split('_')[0]) }))
          .filter((x) => Number.isFinite(x.n))
          .sort((a, b) => b.n - a.n);

        if (candidates.length) {
          latestItem = candidates[0].it;
        } else {
          const itemsWithMetadata = await Promise.all(
            result.items.map(async (item) => {
              try {
                const metadata = await getMetadata(item);
                const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated) : new Date(0);
                return { item, timeCreated };
              } catch {
                return { item, timeCreated: new Date(0) };
              }
            })
          );
          itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
          latestItem = itemsWithMetadata[0].item;
        }

        const url = await getDownloadURL(latestItem);
        setProfilePhotoURL(url);
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ url, ts: Date.now() }));
        } catch {}
      } else {
        setProfilePhotoURL(null);
        try { localStorage.removeItem(cacheKey); } catch {}
      }
    } catch (error) {
      console.error('Erreur récupération photo de profil :', error);
      setProfilePhotoURL(null);
    }
    setProfilePhotoFetched(true);
  }, [user?.uid]);

  // Upload photo de profil
  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!user?.uid || !file) return;
    const safeName = sanitize(file.name);
    const fileRef = ref(storage, `${user.uid}/photo-profil/${Date.now()}_${safeName}`);
    try {
      await uploadBytes(fileRef, file);
      // Invalide le cache pour forcer un refresh propre
      try { localStorage.removeItem(LOGO_CACHE_KEY(user.uid)); } catch {}
      await fetchProfilePhoto();
      setShowPhotoModal(false);
    } catch (error) {
      console.error('Erreur téléchargement photo de profil :', error);
    }
  };

  // Upload combiné pseudo + logo
  const handleSaveCombined = async () => {
    const safePseudo = sanitize(newPseudo.trim());
    if (user?.uid && safePseudo && selectedPhotoFile) {
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, { pseudo: safePseudo, CGV: 'accepted' });
      setUser((prev) => ({ ...(prev || {}), uid: user.uid, pseudo: safePseudo }));

      try {
        const fidelityDocRef = doc(firestore, 'fidelity', user.uid);
        // [COST-OPT] tente cache avant read réseau
        let hasFidelity = false;
        try {
          const c = await getDocFromCache(fidelityDocRef);
          hasFidelity = !!c?.exists();
        } catch {}
        if (!hasFidelity) {
          const s = await getDoc(fidelityDocRef);
          hasFidelity = s.exists();
        }
        if (hasFidelity) {
          await updateDoc(fidelityDocRef, { pseudo: safePseudo });
        }
      } catch (error) {
        console.error('Erreur maj fidelity :', error);
      }

      const safeName = sanitize(selectedPhotoFile.name);
      const fileRef = ref(storage, `${user.uid}/photo-profil/${Date.now()}_${safeName}`);
      try {
        await uploadBytes(fileRef, selectedPhotoFile);
        try { localStorage.removeItem(LOGO_CACHE_KEY(user.uid)); } catch {}
        await fetchProfilePhoto();
      } catch (error) {
        console.error('Erreur téléchargement photo combiné :', error);
      }
      setShowModal(false);
      setShowPhotoModal(false);
    } else {
      alert("Veuillez entrer un nom d'entreprise et sélectionner une image.");
    }
  };

  // Enregistrement du pseudo seul
  const handleSavePseudo = async () => {
    const safePseudo = sanitize(newPseudo.trim());
    if (user?.uid && safePseudo) {
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, { pseudo: safePseudo, CGV: 'accepted' });
      setUser((prev) => ({ ...(prev || {}), uid: user.uid, pseudo: safePseudo }));
      try {
        const fidelityDocRef = doc(firestore, 'fidelity', user.uid);
        // [COST-OPT] cache -> réseau
        let hasFidelity = false;
        try {
          const c = await getDocFromCache(fidelityDocRef);
          hasFidelity = !!c?.exists();
        } catch {}
        if (!hasFidelity) {
          const s = await getDoc(fidelityDocRef);
          hasFidelity = s.exists();
        }
        if (hasFidelity) {
          await updateDoc(fidelityDocRef, { pseudo: safePseudo });
        }
      } catch (error) {
        console.error('Erreur maj fidelity pseudo seul :', error);
      }
      setShowModal(false);
    } else {
      alert("Veuillez entrer un nom d'entreprise valide.");
    }
  };

  // Récupération initiale de la photo
  useEffect(() => {
    if (user?.uid) fetchProfilePhoto();
  }, [user?.uid, fetchProfilePhoto]);

  // Contrôle modale photo
  useEffect(() => {
    if (user && profilePhotoFetched && profilePhotoURL === null) {
      setShowPhotoModal(true);
    } else {
      setShowPhotoModal(false);
    }
  }, [user, profilePhotoURL, profilePhotoFetched]);

  // Mise à jour du localStorage des stats
  useEffect(() => {
    try {
      const next = JSON.stringify({ count, countInMetadata, employeesStats });
      const prev = localStorage.getItem('stats');
      if (prev !== next) localStorage.setItem('stats', next); // [HYBRID-OPT]
    } catch {}
  }, [count, countInMetadata, employeesStats]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let frame = null;
    const updateViewport = () => {
      frame = null;
      setViewportWidth((prev) => {
        const next = window.innerWidth;
        return prev === next ? prev : next; // [HYBRID-OPT]
      });
    };

    const handleResize = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateViewport);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const isSmallScreen = viewportWidth <= 768;
  const isTabletScreen = viewportWidth <= 1024;

  const styles = {
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(15, 35, 56, 0.55)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'var(--eb-surface)',
      padding: '24px',
      borderRadius: 'var(--eb-radius-xl)',
      width: '100%',
      maxWidth: '420px',
      maxHeight: 'calc(100dvh - 32px)',
      overflowY: 'auto',
      textAlign: 'left',
      boxShadow: 'var(--eb-shadow-lg)',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px'
    },
    modalTitle: { margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--eb-text)', textAlign: 'left' },
    input: {},
    button: {},
    legendItem: {
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: 'var(--eb-text-2)'
    },
    legendSquare: {
      display: 'inline-block',
      flex: '0 0 auto',
      width: '12px',
      height: '12px',
      borderRadius: '4px',
      marginRight: '10px'
    }
  };

  const isButtonDisabled = !newPseudo.trim();
  const isCombinedButtonDisabled = !newPseudo.trim() || !selectedPhotoFile;
  const showCombinedModal = showModal && showPhotoModal;

  // Tri et préparation du graphique — 🎨 Camembert propre (épaisseur uniforme)
const sortedEmployees = useMemo(
  () => [...employeesStats].sort((a, b) => (a.pseudo || '').localeCompare(b.pseudo || '')),
  [employeesStats]
);

const userLabel = user?.pseudo || 'Vous';
// eslint-disable-next-line
const chartLabels = [userLabel, ...sortedEmployees.map((e) => e.pseudo || 'Inconnu')];
// eslint-disable-next-line
const chartValues = [count > 0 ? count : 0, ...sortedEmployees.map((e) => e.count || 0)];

// Palette harmonisée au thème (bleu principal + accents)
const THEME_PRIMARY = '#2C6BAA';
const TEAM_STEPS = ['#1F7A8C', '#3BA99C', '#6FB1E4', '#7C3AED', '#F59E0B', '#EF4444', '#10B981'];

// Couleurs homogènes, sans effet de bord inégal
const backgroundColors = useMemo(
  () =>
    chartLabels.map((_, idx) =>
      idx === 0 ? THEME_PRIMARY : TEAM_STEPS[(idx - 1) % TEAM_STEPS.length]
    ),
    // eslint-disable-next-line
  [chartLabels]
);

// Camembert avec coupures régulières et bords uniformes
const pieData = useMemo(
  () => ({
    labels: chartLabels,
    datasets: [
      {
        data: chartValues,
        backgroundColor: backgroundColors,
        borderColor: '#ffffff',   // blanc net pour séparation
        borderWidth: 1.5,         // trait uniforme
        hoverOffset: 8,
        spacing: 0                // pas d'espace irrégulier
      }
    ]
  }),
  [chartLabels, chartValues, backgroundColors] // eslint-disable-line
);

// Légende masquée (tu gardes ta custom) + tooltip %
const options = useMemo(
  () => ({
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const value = Number(ctx.parsed) || 0;
            const total = chartValues.reduce((a, b) => a + b, 0) || 1;
            const pct = Math.round((value / total) * 100);
            return `${label}: ${value.toLocaleString('fr-FR')} (${pct}%)`;
          }
        }
      }
    },
    // transitions douces
    animation: { duration: 600, easing: 'easeOutQuart' },
    layout: { padding: 4 }
  }),
  [chartValues]
);

const total = chartValues.reduce((acc, v) => acc + v, 0);
const employeeContribution = chartValues.slice(1).reduce((acc, v) => acc + v, 0);
const selfInvoices = chartValues[0] || 0;



  const loyaltyStats = useMemo(() => {
    const clients = fidelityData.clients || [];
    let topClientEntry = null;
    for (let i = 0; i < clients.length; i++) { // [HYBRID-OPT]
      const c = clients[i];
      if (!topClientEntry || (c?.totalVisits || 0) > (topClientEntry?.totalVisits || 0)) {
        topClientEntry = c;
      }
    }
    const returningOverall = clients.filter((entry) => (entry?.totalVisits || 0) > 1).length;

    const recentSummary = fidelityData.summary || {};
    const recentVisits = recentSummary.recentVisits || 0;
    const recentClients = recentSummary.recentClients || 0;
    const newClients = recentSummary.newClients || 0;
    const returningRecent = recentSummary.returningClients || 0;
    const previousVisits = recentSummary.previousVisits || 0;
    const retentionRate = recentClients > 0 ? returningRecent / recentClients : 0;
    const trendDiff = recentVisits - previousVisits;
    const trendPct = previousVisits > 0
      ? (trendDiff / previousVisits) * 100
      : recentVisits > 0
      ? 100
      : 0;

    return {
      uniqueClients: fidelityData.uniqueClients || 0,
      totalVisits: fidelityData.total || 0,
      returningClients: returningOverall,
      averagePerClient:
        (fidelityData.uniqueClients || 0) > 0
          ? fidelityData.total / Math.max(fidelityData.uniqueClients, 1)
          : 0,
      topClient: topClientEntry
        ? { name: topClientEntry.label || 'Client', count: topClientEntry.totalVisits || 0 }
        : null,
      recent: {
        visits: recentVisits,
        clients: recentClients,
        newClients,
        returningClients: returningRecent,
        retentionRate,
        retentionRatePct: retentionRate * 100,
        trendDiff,
        trendPct,
        previousVisits,
        bestDay: recentSummary.bestDay || null
      }
    };
  }, [fidelityData]);

  const invoiceShare = total > 0 ? Math.round((employeeContribution / total) * 100) : 0;
  const formattedRecentVisits = loyaltyStats.recent.visits.toLocaleString('fr-FR');
  const formattedActiveClients = loyaltyStats.recent.clients.toLocaleString('fr-FR');
  const formattedNewClients = loyaltyStats.recent.newClients.toLocaleString('fr-FR');
  const totalUniqueClientsFormatted = loyaltyStats.uniqueClients.toLocaleString('fr-FR');
  const returningOverallFormatted = loyaltyStats.returningClients.toLocaleString('fr-FR');
  const currentDayUniqueClientsFormatted = (fidelityData.summary?.currentDayUniqueClients || 0).toLocaleString('fr-FR');
  const currentDayNewClientsFormatted = (fidelityData.summary?.currentDayNewClients || 0).toLocaleString('fr-FR');
  const loyaltyTotalValue = isPointsProgram ? fidelityData.totalPointsEarned || 0 : fidelityData.totalEvents || 0;
  const loyaltyTotalValueNumber = Number(loyaltyTotalValue) || 0;
  const formattedLoyaltyTotalValue = loyaltyTotalValueNumber.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: isPointsProgram ? 2 : 0
  });
  const loyaltyValueLabel = isPointsProgram
    ? (Math.abs(loyaltyTotalValueNumber) > 1 ? 'points cumulés' : 'point cumulé')
    : (Math.abs(loyaltyTotalValueNumber) > 1 ? 'passages cumulés' : 'passage cumulé');
  const retentionRatePct = Number.isFinite(loyaltyStats.recent.retentionRatePct)
    ? Math.round(loyaltyStats.recent.retentionRatePct)
    : 0;
  const trendDescriptor = (() => {
    if (loyaltyStats.recent.previousVisits === 0) {
      if (loyaltyStats.recent.visits === 0) return 'Pas de période précédente';
      return 'Nouvelle activité enregistrée';
    }
    const pct = loyaltyStats.recent.trendPct || 0;
    const diff = loyaltyStats.recent.trendDiff || 0;
    if (diff === 0) return 'Stable vs période précédente';
    const pctText = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
    return `${pctText} vs période précédente`;
  })();
  const totalVotes = useMemo(
    () => Object.values(satisfactionData.counts || {}).reduce((acc, curr) => acc + curr, 0),
    [satisfactionData]
  );
  const recentFeedbacks = useMemo(() => {
    return feedbacks
      .map((fb) => {
        const safeComment = typeof fb.comment === 'string' ? sanitize(fb.comment).trim() : '';
        const createdAt = fb.createdAt instanceof Date ? fb.createdAt : resolveFeedbackDate(fb);
        const sortTs =
          fb.sortTs ||
          createdAt?.getTime() ||
          (typeof fb.txDateMillis === 'number' ? fb.txDateMillis : 0) ||
          0;
        return {
          id: fb.id,
          rating: fb.rating,
          comment: safeComment,
          createdAt,
          sortTs
        };
      })
      .filter((fb) => fb.comment.length > 0)
      .sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0));
  }, [feedbacks]);
  const satisfactionAverage =
    satisfactionData.average && satisfactionData.average > 0
      ? satisfactionData.average.toFixed(1)
      : '—';
  const formatFeedbackDate = useCallback((date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }, []);

  const heroStats = [
    { label: 'Satisfaction', value: satisfactionAverage, hint: `${totalVotes} avis` },
    { label: 'Visites totales (30j)', value: formattedRecentVisits, hint: trendDescriptor },
    { label: 'Nouveaux clients (30j)', value: formattedNewClients, hint: `${formattedActiveClients} clients actifs` }
  ];

  const metricCards = useMemo(() => {
    const recent = loyaltyStats.recent;
    const recentClients = recent.clients || 0;
    const newClients = recent.newClients || 0;
    const returningRecent = recent.returningClients || 0;

    return [
      {
        label: 'Clients uniques (30j)',
        value: recentClients,
        hint: `${newClients.toLocaleString('fr-FR')} nouveaux sur la période`
      },
      {
        label: 'Taux de fidélisation (30j)',
        value: `${retentionRatePct}%`,
        hint: `${returningRecent.toLocaleString('fr-FR')} clients récurrents`
      },
      {
        label: 'Factures totales',
        value: total,
        hint: `${selfInvoices.toLocaleString('fr-FR')} importées par vous • ${countInMetadata.toLocaleString('fr-FR')} catégorisées`
      },
      {
        label: 'Part collaborateurs',
        value: `${invoiceShare}%`,
        hint: `${employeeContribution.toLocaleString('fr-FR')} factures équipe`
      }
    ];
  }, [
    loyaltyStats.recent,
    retentionRatePct,
    total,
    selfInvoices,
    countInMetadata,
    invoiceShare,
    employeeContribution
  ]);

  const loyaltyChartConfig = useMemo(() => {
    const timeline = fidelityData.timeline || { labels: [], counts: [] };
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false }
      },
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0 } },
        y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 }, grid: { color: 'rgba(148, 163, 184, 0.2)' } }
      }
    };

    if (!timeline.labels.length) {
      const todayLabel = new Date().toISOString().split('T')[0];
      return {
        isEmpty: true,
        data: {
          labels: [todayLabel],
          datasets: [
            {
              label: 'Passages fidélité',
              data: [0],
              borderColor: 'rgba(44, 96, 152, 1)',
              backgroundColor: 'rgba(34, 76, 120, 0.15)',
              fill: true,
              tension: 0.25,
              pointRadius: 0
            }
          ]
        },
        options: baseOptions
      };
    }

    return {
      isEmpty: false,
      data: {
        labels: timeline.labels,
        datasets: [
          {
            label: 'Passages fidélité',
            data: timeline.counts,
            borderColor: 'rgba(44, 96, 152, 1)',
            backgroundColor: 'rgba(39, 76, 116, 0.15)',
            fill: true,
            tension: 0.25,
            pointRadius: (context) => (context.raw > 0 ? 4 : 0),
            pointHoverRadius: 6
          }
        ]
      },
      options: baseOptions
    };
  }, [fidelityData.timeline]);

  const loyaltyHighlightRows = [
    { label: 'Clients uniques (total)', value: totalUniqueClientsFormatted },
    { label: 'Clients récurrents (total)', value: returningOverallFormatted },
    { label: "Clients uniques (aujourd'hui)", value: currentDayUniqueClientsFormatted },
    { label: "Nouveaux clients (aujourd'hui)", value: currentDayNewClientsFormatted }
  ];

  const isSmallScreenMemo = isSmallScreen;
  const isTabletScreenMemo = isTabletScreen;

  const heroName = user?.pseudo || 'Votre entreprise';
  const heroInitials =
    heroName.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || 'EC';

  const dashboardStyles = useMemo(() => {
    const avatarSize = isSmallScreenMemo ? 52 : 96;
    const card = {
      backgroundColor: 'var(--eb-surface)',
      border: '1px solid var(--eb-border)',
      borderRadius: 'var(--eb-radius-lg)',
      boxShadow: 'var(--eb-shadow-sm)'
    };
    return {
      summaryRow: {
        width: '100%',
        marginBottom: '20px'
      },
      heroCard: {
        padding: isSmallScreenMemo ? '20px' : '28px',
        borderRadius: 'var(--eb-radius-lg)',
        background: 'linear-gradient(135deg, var(--eb-brand) 0%, var(--eb-brand-800) 100%)',
        color: '#ffffff',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: isSmallScreenMemo ? 'flex-start' : 'center',
        gap: isSmallScreenMemo ? '12px' : '24px',
        boxShadow: 'var(--eb-shadow-md)'
      },
      heroInfo: { flex: '1 1 auto', minWidth: 0 },
      heroTitle: { fontSize: isSmallScreenMemo ? '20px' : '24px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '4px', overflowWrap: 'anywhere' },
      heroSubtitle: { fontSize: '14px', opacity: 0.8 },
      heroStatsRow: {
        display: 'grid',
        gridTemplateColumns: isSmallScreenMemo ? '1fr' : 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: isSmallScreenMemo ? '10px' : '12px',
        marginTop: '20px',
        width: '100%'
      },
      heroStat: isSmallScreenMemo ? {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gridTemplateAreas: "'label value' 'hint value'",
        alignItems: 'center',
        columnGap: '12px',
        padding: '12px 14px',
        borderRadius: 'var(--eb-radius)',
        backgroundColor: 'rgba(255, 255, 255, 0.10)',
        border: '1px solid rgba(255, 255, 255, 0.14)'
      } : { padding: '14px 16px', borderRadius: 'var(--eb-radius)', backgroundColor: 'rgba(255, 255, 255, 0.10)', border: '1px solid rgba(255, 255, 255, 0.14)' },
      heroStatValue: { gridArea: 'value', fontSize: isSmallScreenMemo ? '20px' : '24px', fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
      heroStatLabel: { gridArea: 'label', fontSize: '13px', opacity: 0.85, marginTop: '2px', fontWeight: 500 },
      heroStatHint: { gridArea: 'hint', fontSize: '12px', opacity: 0.7, marginTop: isSmallScreenMemo ? '2px' : '4px' },
      heroAvatarWrapper: {
        width: `${avatarSize}px`,
        height: `${avatarSize}px`,
        flex: '0 0 auto',
        borderRadius: 'var(--eb-radius-lg)',
        backgroundColor: 'rgba(255, 255, 255, 0.14)',
        border: '1px solid rgba(255, 255, 255, 0.22)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      heroAvatar: { width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#ffffff' },
      heroAvatarFallback: { fontSize: isSmallScreenMemo ? '18px' : '32px', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.02em' },
      metricsGrid: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: isSmallScreenMemo ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: isSmallScreenMemo ? '12px' : '16px',
        marginBottom: '20px'
      },
      metricCard: {
        ...card,
        minWidth: 0,
        padding: isSmallScreenMemo ? '16px' : '20px'
      },
      metricLabel: { fontSize: '13.5px', color: 'var(--eb-muted)', marginBottom: '8px', fontWeight: 500 },
      metricValue: { fontSize: isSmallScreenMemo ? '24px' : '28px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--eb-text)', fontVariantNumeric: 'tabular-nums' },
      metricHint: { fontSize: '13px', marginTop: '6px', color: 'var(--eb-muted)' },
      chartRow: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: isTabletScreenMemo ? '1fr' : 'minmax(0, 2fr) minmax(260px, 1fr)',
        gap: '16px',
        marginBottom: '20px'
      },
      chartCard: {
        ...card,
        padding: isSmallScreenMemo ? '20px' : '24px',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: isSmallScreenMemo ? 'auto' : '340px'
      },
      chartHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 12px',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '16px'
      },
      chartTitle: { fontSize: '17px', fontWeight: 650, color: 'var(--eb-text)' },
      chartSub: { fontSize: '13px', color: 'var(--eb-muted)' },
      chartInner: { flex: '1 1 auto', position: 'relative', minHeight: isSmallScreenMemo ? '220px' : '240px' },
      chartEmpty: { textAlign: 'center', color: 'var(--eb-muted)', fontSize: '14px', marginTop: '12px' },
      highlightCard: {
        ...card,
        padding: isSmallScreenMemo ? '16px' : '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      },
      highlightRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: 'var(--eb-radius)',
        backgroundColor: 'var(--eb-surface-2)',
        border: '1px solid var(--eb-border)'
      },
      highlightLabel: { fontSize: '13.5px', color: 'var(--eb-text-2)', fontWeight: 500 },
      highlightValue: { fontSize: '20px', fontWeight: 700, color: 'var(--eb-text)', fontVariantNumeric: 'tabular-nums' },
      highlightBadge: { fontSize: '12px', color: 'var(--eb-success)', backgroundColor: 'var(--eb-success-bg)', borderRadius: 'var(--eb-radius-pill)', padding: '4px 10px' }
    };
  }, [isSmallScreenMemo, isTabletScreenMemo]);

  const sectionStyles = useMemo(() => {
    const baseCard = {
      margin: 0,
      width: 'auto',
      maxWidth: 'none',
      borderRadius: 'var(--eb-radius-lg)',
      background: 'var(--eb-surface)',
      border: '1px solid var(--eb-border)',
      boxShadow: 'var(--eb-shadow-sm)',
      position: 'relative',
      overflow: 'hidden',
      display: 'block',
      minWidth: 0,
      textAlign: 'left',
      padding: isSmallScreen ? '20px' : '24px'
    };
    return {
      multiCardRow: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '16px',
        alignItems: 'start'
      },
      satisfactionCard: { ...baseCard },
      fidelityCard: { ...baseCard },
      invoicesCard: { ...baseCard },
      cardPattern: { display: 'none' },
      cardHeader: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '16px'
      },
      cardKicker: {
        fontSize: '12px',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--eb-muted)',
        fontWeight: 600,
        marginBottom: '2px'
      },
      cardTitle: {
        fontSize: '17px',
        fontWeight: 650,
        color: 'var(--eb-text)'
      },
      cardDivider: {
        width: '100%',
        height: '1px',
        background: 'var(--eb-border)',
        marginBottom: '18px'
      },
      satisfactionHeader: { textAlign: 'center', fontSize: '18px', fontWeight: 700, marginBottom: '15px' },
      satisfactionContent: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: isSmallScreen ? '16px' : '32px' },
      satisfactionStarWrapper: { position: 'relative', lineHeight: 0 },
      satisfactionLegend: { fontSize: '14px', color: 'var(--eb-text-2)', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '6px', fontVariantNumeric: 'tabular-nums' },
      feedbackContainer: { marginTop: '20px', width: '100%' },
      feedbackHeading: { fontSize: '15px', fontWeight: 650, marginBottom: '10px', color: 'var(--eb-text)', display: 'flex', alignItems: 'center', gap: '8px' },
      feedbackHeadingBadge: {
        fontSize: '12px',
        fontWeight: 600,
        color: 'var(--eb-brand)',
        backgroundColor: 'var(--eb-brand-50)',
        border: '1px solid var(--eb-brand-100)',
        borderRadius: 'var(--eb-radius-pill)',
        padding: '2px 10px'
      },
      feedbackList: { display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: isSmallScreen ? '260px' : '240px', overflowY: 'auto', paddingRight: '4px' },
      feedbackEmpty: { fontSize: '14px', color: 'var(--eb-muted)', textAlign: 'center', padding: '16px', borderRadius: 'var(--eb-radius)', border: '1px dashed var(--eb-border-strong)', backgroundColor: 'var(--eb-surface-2)' },
      feedbackItem: { borderRadius: 'var(--eb-radius)', border: '1px solid var(--eb-border)', padding: '12px 14px', backgroundColor: 'var(--eb-surface-2)' },
      feedbackItemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, color: 'var(--eb-text)', fontSize: '14px' },
      feedbackItemMeta: { fontSize: '13px', color: 'var(--eb-muted)', marginLeft: '12px', fontWeight: 500 },
      feedbackRatingRow: { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' },
      feedbackSource: { fontSize: '13px', color: 'var(--eb-text)', fontWeight: 600 },
      feedbackComment: { marginTop: '8px', fontSize: '14px', color: 'var(--eb-text-2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
      feedbackCommentMuted: { marginTop: '8px', fontSize: '13px', color: 'var(--eb-muted)', fontStyle: 'italic' },
      fidelityListWrapper: { marginBottom: '4px' },
      fidelityList: { listStyleType: 'none', textAlign: 'left', display: 'block', padding: 0, margin: 0, width: '100%' },
      fidelityListItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        borderRadius: 'var(--eb-radius-sm)',
        backgroundColor: 'var(--eb-surface-2)',
        border: '1px solid var(--eb-border)',
        color: 'var(--eb-text)',
        fontSize: '14px',
        marginBottom: '8px',
        overflowWrap: 'anywhere'
      },
      fidelityBadge: {
        flex: '0 0 auto',
        fontSize: '12.5px',
        fontWeight: 600,
        color: 'var(--eb-brand)',
        padding: '2px 10px',
        borderRadius: 'var(--eb-radius-pill)',
        backgroundColor: 'var(--eb-brand-50)',
        border: '1px solid var(--eb-brand-100)'
      },
      invoicesContent: {
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '16px'
      },
      invoicesChart: { width: '100%', maxWidth: '260px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' },
      invoicesLegend: { width: '100%', textAlign: 'left' },
      invoicesLegendList: { listStyle: 'none', padding: 0, margin: 0, display: 'block', textAlign: 'left' }
    };
  }, [isSmallScreen]);

  const satisfactionStarSize = isSmallScreen ? 80 : 100;
  const satisfactionStarValueSize = isSmallScreen ? '22px' : '26px';

  return (
    <div className="dashboard-container">
      <MerchantNavbar />

      {showCombinedModal ? (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} role="dialog" aria-modal="true">
            <h2 style={styles.modalTitle}>Informations nécessaires</h2>
            <input
              type="text"
              className="eb-input"
              value={newPseudo}
              onChange={(e) => setNewPseudo(e.target.value)}
              placeholder="Nom de votre entreprise"
            />
            <button
              type="button"
              className="eb-btn eb-btn--secondary eb-btn--block"
              onClick={() => fileInputRef.current.click()}
            >
              Importez votre logo
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => setSelectedPhotoFile(e.target.files[0])}
              accept="image/*"
            />
            {selectedPhotoFile && (
              <p className="eb-muted" style={{ margin: 0, fontSize: '14px', overflowWrap: 'anywhere' }}>
                Importé : {selectedPhotoFile.name}
              </p>
            )}
            <button
              className="pseudo-save-button eb-btn eb-btn--primary eb-btn--block"
              onClick={handleSaveCombined}
              disabled={isCombinedButtonDisabled}
            >
              Enregistrer
            </button>
          </div>
        </div>
      ) : (
        <>
          {showModal && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent} role="dialog" aria-modal="true">
                <h2 style={styles.modalTitle}>Nom de votre entreprise</h2>
                <input
                  type="text"
                  className="eb-input"
                  value={newPseudo}
                  onChange={(e) => setNewPseudo(e.target.value)}
                  placeholder="Votre nom d'entreprise"
                />
                <button
                  className="pseudo-save-button eb-btn eb-btn--primary eb-btn--block"
                  onClick={handleSavePseudo}
                  disabled={isButtonDisabled}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          )}
          {showPhotoModal && (
            <div style={styles.modalOverlay}>
              <div style={styles.modalContent} role="dialog" aria-modal="true">
                <h2 style={styles.modalTitle}>Logo</h2>
                <p className="eb-muted" style={{ margin: 0 }}>Veuillez importer votre logo</p>
                <button
                  type="button"
                  className="eb-btn eb-btn--primary eb-btn--block"
                  onClick={() => fileInputRef.current.click()}
                >
                  Choisir une image
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleProfilePhotoChange}
                  accept="image/*"
                />
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ width: '100%', minWidth: 0 }}>
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title" style={{ textAlign: 'left', margin: 0 }}>Accueil</h1>
            <p className="eb-page-subtitle">Vue d'ensemble de votre activité</p>
          </div>
        </header>

        {showStripeModal ? (
          <div className="eb-card eb-card--pad" style={{ width: '100%' }}>
            <div className="secret-key-section">
              <h2 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, textAlign: 'left' }}>
                Activez votre compte entreprise
              </h2>
              <ol style={{ margin: '16px 0 0', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '14px', color: 'var(--eb-text-2)', fontSize: '15px' }}>
                <li>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 14px' }}>
                    <span>Activez votre compte :</span>
                    <button
                      className="eb-btn eb-btn--primary"
                      onClick={() => {
                        const stripeUrl =
                          process.env.NODE_ENV === 'production'
                            ? 'https://buy.stripe.com/fZeg04cUh6Z6b3W144'
                            : 'https://buy.stripe.com/test_00gaI28o69U1adidQQ';
                        window.open(stripeUrl, '_blank');
                      }}
                    >
                      Activer mon compte
                    </button>
                  </div>
                </li>
                <li>Activation sous 24-48 h après votre paiement</li>
                <li>Profitez pleinement de votre compte entreprise !</li>
              </ol>
            </div>
          </div>
        ) : (
          <>
            <div style={dashboardStyles.summaryRow}>
              <div style={dashboardStyles.heroCard}>
                <div style={dashboardStyles.heroInfo}>
                  <div style={dashboardStyles.heroTitle}>{heroName}</div>
                  <div style={dashboardStyles.heroSubtitle}>
                    Synthèse de vos performances en temps réel
                  </div>
                  <div style={dashboardStyles.heroStatsRow}>
                    {heroStats.map((stat) => (
                      <div key={stat.label} style={dashboardStyles.heroStat}>
                        <div style={dashboardStyles.heroStatValue}>{stat.value}</div>
                        <div style={dashboardStyles.heroStatLabel}>{stat.label}</div>
                        {stat.hint && (
                          <div style={dashboardStyles.heroStatHint}>{stat.hint}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={dashboardStyles.heroAvatarWrapper}>
                  {profilePhotoURL ? (
                    <img
                      src={profilePhotoURL}
                      alt="Logo entreprise"
                      style={dashboardStyles.heroAvatar}
                    />
                  ) : (
                    <div style={dashboardStyles.heroAvatarFallback}>{heroInitials}</div>
                  )}
                </div>
              </div>
            </div>

            <div style={dashboardStyles.metricsGrid}>
              {metricCards.map((card) => (
                <div key={card.label} style={dashboardStyles.metricCard}>
                  <div style={dashboardStyles.metricLabel}>{card.label}</div>
                  <div style={dashboardStyles.metricValue}>
                    {typeof card.value === 'number'
                      ? card.value.toLocaleString('fr-FR')
                      : card.value}
                  </div>
                  <div style={dashboardStyles.metricHint}>{card.hint}</div>
                </div>
              ))}
            </div>

            <div style={dashboardStyles.chartRow}>
              <div style={dashboardStyles.chartCard}>
                <div style={dashboardStyles.chartHeader}>
                  <div style={dashboardStyles.chartTitle}>
                    Évolution des passages fidélité
                  </div>
                  <div style={dashboardStyles.chartSub}>Derniers enregistrements</div>
                </div>
                <div style={dashboardStyles.chartInner}>
                  <Line
                    data={loyaltyChartConfig.data}
                    options={loyaltyChartConfig.options}
                  />
                </div>
                {loyaltyChartConfig.isEmpty && (
                  <div style={dashboardStyles.chartEmpty}>
                    Pas encore de passages enregistrés
                  </div>
                )}
              </div>

              <div style={dashboardStyles.highlightCard}>
                {loyaltyHighlightRows.map((row) => (
                  <div key={row.label} style={dashboardStyles.highlightRow}>
                    <span style={dashboardStyles.highlightLabel}>{row.label}</span>
                    <span style={dashboardStyles.highlightValue}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={sectionStyles.multiCardRow}>
              <div
                className="statsrecap-container"
                style={sectionStyles.satisfactionCard}
              >
                <span
                  aria-hidden="true"
                  style={sectionStyles.cardPattern}
                />
                <div style={sectionStyles.cardHeader}>
                  <div>
                    <div style={sectionStyles.cardKicker}>Relation client</div>
                    <div style={sectionStyles.cardTitle}>Satisfaction clients</div>
                  </div>
                </div>
                <div style={sectionStyles.cardDivider} />
                <div
                  style={{
                    fontSize: '14px',
                    color: 'var(--eb-muted)',
                    marginBottom: '16px'
                  }}
                >
                  {totalVotes} {totalVotes > 1 ? 'votes cumulés' : 'vote cumulé'}
                </div>
                <div style={sectionStyles.satisfactionContent}>
                  <div style={sectionStyles.satisfactionStarWrapper}>
                    <FaStar size={satisfactionStarSize} color="#F5B301" />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: satisfactionStarValueSize,
                        fontWeight: 700,
                        lineHeight: 1,
                        color: 'var(--eb-text)'
                      }}
                    >
                      {satisfactionAverage}
                    </div>
                  </div>
                  <div style={sectionStyles.satisfactionLegend}>
                    <div>5 étoiles : {satisfactionData.counts[5] || 0}</div>
                    <div>4 étoiles : {satisfactionData.counts[4] || 0}</div>
                    <div>3 étoiles : {satisfactionData.counts[3] || 0}</div>
                    <div>2 étoiles : {satisfactionData.counts[2] || 0}</div>
                    <div>1 étoile : {satisfactionData.counts[1] || 0}</div>
                  </div>
                </div>
                <div style={sectionStyles.feedbackContainer}>
                  <div style={sectionStyles.feedbackHeading}>
                    Avis récents
                    <span style={sectionStyles.feedbackHeadingBadge}>
                      {recentFeedbacks.length || 0}{' '}
                      reçu{recentFeedbacks.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  {recentFeedbacks.length === 0 ? (
                    <div style={sectionStyles.feedbackEmpty}>Aucun avis pour le moment</div>
                  ) : (
                    <div style={sectionStyles.feedbackList}>
                      {recentFeedbacks.map((fb) => (
                        <div key={fb.id} style={sectionStyles.feedbackItem}>
                          <div style={sectionStyles.feedbackItemHeader}>
                            <span style={sectionStyles.feedbackSource}>Avis client</span>
                            <span style={sectionStyles.feedbackItemMeta}>{formatFeedbackDate(fb.createdAt)}</span>
                          </div>
                          <div style={sectionStyles.feedbackRatingRow}>
                            {STAR_KEYS.map((star) => (
                              <FaStar
                                key={star}
                                size={14}
                                color={fb.rating >= star ? '#F5B301' : 'var(--eb-border-strong)'}
                              />
                            ))}
                            
                          </div>
                          {fb.comment && (
                            <div style={sectionStyles.feedbackComment}>{fb.comment}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div
                className="statsrecap-container"
                style={sectionStyles.fidelityCard}
              >
                <span
                  aria-hidden="true"
                  style={{
                    ...sectionStyles.cardPattern,
                    right: '-80px'
                  }}
                />
                <div style={sectionStyles.cardHeader}>
                  <div>
                    <div style={sectionStyles.cardKicker}>Fidélité</div>
                    <div style={sectionStyles.cardTitle}>Programme fidélité</div>
                  </div>
                </div>
                <div style={sectionStyles.cardDivider} />
                <div
                  style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    marginBottom: '16px',
                    color: 'var(--eb-text)'
                  }}
                >
                  {formattedLoyaltyTotalValue}{' '}
                  {loyaltyValueLabel}
                </div>
                <div style={sectionStyles.fidelityListWrapper}>
                  {sortedFidelityEntries.length > 0 ? (
                    <ul
                      style={sectionStyles.fidelityList}
                    >
                      {sortedFidelityEntries.map((entry) => {
                        const rawValue = Number(entry.count) || 0;
                        const formattedValue = rawValue.toLocaleString('fr-FR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: isPointsProgram ? 2 : 0
                        });
                        const unitLabel = isPointsProgram
                          ? (Math.abs(rawValue) > 1 ? 'points' : 'point')
                          : (Math.abs(rawValue) > 1 ? 'passages' : 'passage');
                        return (
                          <li key={entry.uid} style={sectionStyles.fidelityListItem}>
                            <span>{entry.pseudo}</span>
                            <span style={sectionStyles.fidelityBadge}>
                              {formattedValue} {unitLabel}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div
                      style={{
                        color: 'var(--eb-muted)',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '16px',
                        borderRadius: 'var(--eb-radius)',
                        border: '1px dashed var(--eb-border-strong)',
                        backgroundColor: 'var(--eb-surface-2)'
                      }}
                    >
                      Aucune donnée disponible
                    </div>
                  )}
                </div>
              </div>

              <div
                className="statsrecap-container"
                style={sectionStyles.invoicesCard}
              >
                <span
                  aria-hidden="true"
                  style={{
                    ...sectionStyles.cardPattern,
                    right: '-40px'
                  }}
                />
                <div style={sectionStyles.cardHeader}>
                  <div>
                    <div style={sectionStyles.cardKicker}>Facturation</div>
                    <div style={sectionStyles.cardTitle}>Statistiques factures</div>
                  </div>
                </div>
                <div style={sectionStyles.cardDivider} />
                {total === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      fontSize: '14px',
                      padding: '16px',
                      color: 'var(--eb-muted)',
                      borderRadius: 'var(--eb-radius)',
                      border: '1px dashed var(--eb-border-strong)',
                      backgroundColor: 'var(--eb-surface-2)'
                    }}
                  >
                    Aucune donnée disponible
                  </div>
                ) : (
                  <div style={sectionStyles.invoicesContent}>
                    <div style={sectionStyles.invoicesChart}>
                      <Pie data={pieData} options={options} />
                      <div
                        style={{
                          textAlign: 'center',
                          marginTop: '12px',
                          fontSize: '15px',
                          color: 'var(--eb-text)'
                        }}
                      >
                        <strong>
                          Total = {total.toLocaleString('fr-FR')}{' '}
                          {total > 1 ? 'factures' : 'facture'}
                        </strong>
                      </div>
                    </div>
                    <div style={sectionStyles.invoicesLegend}>
                      <ul
                        style={sectionStyles.invoicesLegendList}
                      >
                        <li style={styles.legendItem}>
                          <span
                            style={{
                              ...styles.legendSquare,
                              backgroundColor: backgroundColors[0]
                            }}
                          ></span>
                          {userLabel} : {chartValues[0]}{' '}
                          {chartValues[0] > 1 ? 'factures' : 'facture'}
                        </li>
                        {sortedEmployees.map((emp, idx) => {
                          const colorIndex = idx + 1;
                          return (
                            <li key={emp.pseudo || idx} style={styles.legendItem}>
                              <span
                                style={{
                                  ...styles.legendSquare,
                                  backgroundColor: backgroundColors[colorIndex]
                                }}
                              ></span>
                              {emp.pseudo} : {emp.count}{' '}
                              {emp.count > 1 ? 'factures' : 'facture'}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MerchantDashboard;
